import { useState, useCallback, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Card, Button, Badge } from '@medikei/ui';
import { Skeleton } from '@/components/ui/Skeleton';
import { AvatarUploader, CoverPhotoUploader } from '@/components/files';
import { DoctorDocumentsSection, DoctorTargetAudienceSection, DoctorProfilePreviewModal } from '@/components/doctor';
import {
  useDoctorProfile,
  doctorsApi,
  useDoctorSpecialties,
  useAddDoctorSpecialty,
  useRemoveDoctorSpecialty,
  useSetDoctorPrimarySpecialty,
  useAuth,
} from '@/hooks';
import type { UploadedFile, DoctorProfile } from '@/api/http-client';
import { useToast } from '@/components/ui/use-toast';
import { filesApi } from '@/api';
import {
  ArrowLeft,
  Save,
  Shield,
  CheckCircle,
  Clock,
  XCircle,
  FileText,
  Trash2,
  Stethoscope,
  MapPin,
  Navigation,
  Home,
  Video,
  Building,
  Upload,
  Paperclip,
  Sparkles,
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';

const LANGUAGES = [
  'Português',
  'Inglês',
  'Espanhol',
  'Francês',
  'Alemão',
  'Italiano',
];

interface IdentitySectionProps {
  crm: string;
  crmState: string;
  status: string;
  loading?: boolean;
}

function IdentitySection({ crm, crmState, status, loading }: IdentitySectionProps) {
  const getStatusBadge = (statusValue: string) => {
    switch (statusValue) {
      case 'APPROVED':
        return <Badge className="bg-green-500 text-white"><CheckCircle className="h-3 w-3 mr-1" /> Aprovado</Badge>;
      case 'PENDING':
        return <Badge className="bg-yellow-500 text-white"><Clock className="h-3 w-3 mr-1" /> Pendente</Badge>;
      case 'UNDER_REVIEW':
        return <Badge className="bg-blue-500 text-white"><Clock className="h-3 w-3 mr-1" /> Em Análise</Badge>;
      case 'REJECTED':
        return <Badge className="bg-red-500 text-white"><XCircle className="h-3 w-3 mr-1" /> Rejeitado</Badge>;
      case 'BLOCKED':
        return <Badge className="bg-red-500 text-white"><XCircle className="h-3 w-3 mr-1" /> Bloqueado</Badge>;
      default:
        return <Badge variant="outline">{statusValue || 'Em Análise'}</Badge>;
    }
  };

  if (loading) {
    return (
      <Card className="p-6 bg-card border-border">
        <Skeleton className="h-6 w-32 mb-4" />
        <div className="space-y-3">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6 bg-card border-border">
      <div className="flex items-center gap-2 mb-4">
        <Shield className="text-primary-500 h-5 w-5" />
        <h3 className="font-semibold text-foreground">Identidade</h3>
        <Badge variant="outline" className="ml-auto">
          Apenas leitura
        </Badge>
      </div>
      <div className="space-y-4">
        <div className="flex items-center justify-between py-2 border-b border-border">
          <span className="text-muted-foreground">CRM</span>
          <span className="font-medium text-foreground">
            {crm || 'Não informado'} {crmState ? `/ ${crmState}` : ''}
          </span>
        </div>
        <div className="flex items-center justify-between py-2 border-b border-border">
          <span className="text-muted-foreground">Status do Cadastro</span>
          {getStatusBadge(status)}
        </div>
        <div className="flex items-center justify-between py-2 border-b border-border">
          <span className="text-muted-foreground">Validação CFM</span>
          <span className="inline-flex items-center gap-1 font-semibold text-xs text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-2.5 py-1 rounded-full border border-emerald-500/20">
            <CheckCircle className="h-3 w-3" /> Validado no CFM
          </span>
        </div>
      </div>
      <p className="text-sm text-muted-foreground mt-4">
        Os dados de identidade são regulatórios e vinculados ao seu registro profissional.
      </p>
    </Card>
  );
}

interface ProfileFormData {
  displayName: string;
  tagline: string;
  bio: string;
  coverUrl?: string | null;
  languages: string[];
  awards: string[];
  publications: string;
  minAge?: number | null;
  maxAge?: number | null;
  allowedAgeGroups?: string[];
  hasTelemedicine?: boolean;
  hasInPerson?: boolean;
  hasHomeCare?: boolean;
  clinicName?: string;
  clinicAddress?: string;
  clinicNumber?: string;
  clinicComplement?: string;
  clinicNeighborhood?: string;
  clinicCity?: string;
  clinicState?: string;
  clinicCep?: string;
  homeCareRadiusKm?: number;
  inPersonRadiusKm?: number;
}

interface ProfileSectionProps {
  data: ProfileFormData;
  onChange: (data: Partial<ProfileFormData>) => void;
  loading?: boolean;
  photoUrl: string | null;
  onPhotoChange: (url: string) => void;
  coverUrl: string | null;
  onCoverChange: (url: string) => void;
  doctorId: string;
}

function ProfileSection({
  data,
  onChange,
  loading,
  photoUrl,
  onPhotoChange,
  coverUrl,
  onCoverChange,
  doctorId,
}: ProfileSectionProps) {
  const toggleLanguage = (lang: string) => {
    const newLanguages = data.languages.includes(lang)
      ? data.languages.filter((l) => l !== lang)
      : [...data.languages, lang];
    onChange({ languages: newLanguages });
  };

  if (loading) {
    return (
      <Card className="p-6 bg-card border-border">
        <Skeleton className="h-6 w-32 mb-4" />
        <div className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6 bg-card border-border">
      <div className="flex items-center gap-2 mb-6">
        <FileText className="text-primary-500 h-5 w-5" />
        <h3 className="font-semibold text-foreground">Perfil Público</h3>
        <Badge variant="outline" className="ml-auto bg-green-500/10 text-green-600 border-green-500/30">
          Edição livre
        </Badge>
      </div>

      {/* Upload e Ajuste de Imagem de Capa Panorâmica */}
      <div className="mb-6 pb-6 border-b border-border">
        <CoverPhotoUploader
          currentUrl={coverUrl}
          onCoverChange={onCoverChange}
          ownerId={doctorId}
        />
      </div>

      <div className="flex items-start gap-6 mb-6">
        <AvatarUploader
          ownerId={doctorId || 'doctor'}
          ownerType="DOCTOR"
          currentUrl={photoUrl}
          onUploadSuccess={(file: UploadedFile) => {
            const finalKeyOrUrl = file.key || file.url || file.id;
            onPhotoChange(finalKeyOrUrl);
          }}
          onDeleteSuccess={() => {
            onPhotoChange('');
          }}
          category="PROFILE_PHOTO"
          visibility="PUBLIC"
          size="lg"
        />
        <div className="flex-1 space-y-1">
          <p className="text-sm font-semibold text-foreground">Foto de Perfil</p>
          <p className="text-sm text-muted-foreground">
            Foto profissional de rosto com ajuste de zoom e corte circular em tempo real.
          </p>
          <p className="text-xs text-muted-foreground">
            Formatos: JPG, PNG, WebP. Máximo 5MB (Recomendado 400x400px).
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">Nome de Exibição</label>
          <input
            type="text"
            value={data.displayName}
            onChange={(e) => onChange({ displayName: e.target.value })}
            maxLength={100}
            placeholder="Como você quer que os pacientes te vejam"
            className="w-full px-3 py-2 border rounded-lg bg-background border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
          />
          <p className="text-xs text-muted-foreground mt-1">{data.displayName.length}/100 caracteres</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-1">Tagline</label>
          <input
            type="text"
            value={data.tagline}
            onChange={(e) => onChange({ tagline: e.target.value })}
            maxLength={200}
            placeholder="Uma frase que te define como profissional"
            className="w-full px-3 py-2 border rounded-lg bg-background border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
          />
          <p className="text-xs text-muted-foreground mt-1">{data.tagline.length}/200 caracteres</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-1">Bio</label>
          <textarea
            value={data.bio}
            onChange={(e) => onChange({ bio: e.target.value })}
            maxLength={2000}
            rows={4}
            placeholder="Conte um pouco sobre sua trajetória e especializações"
            className="w-full px-3 py-2 border rounded-lg bg-background border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none transition-colors"
          />
          <p className="text-xs text-muted-foreground mt-1">{data.bio.length}/2000 caracteres</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Idiomas</label>
          <div className="flex flex-wrap gap-2">
            {LANGUAGES.map((lang) => (
              <button
                type="button"
                key={lang}
                onClick={() => toggleLanguage(lang)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  data.languages.includes(lang)
                    ? 'bg-primary-500 text-white'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {lang}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-1">Prêmios e Conquistas</label>
          <input
            type="text"
            value={data.awards.join(', ')}
            onChange={(e) => onChange({ awards: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
            placeholder="Separados por vírgula"
            className="w-full px-3 py-2 border rounded-lg bg-background border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
          />
          <p className="text-xs text-muted-foreground mt-1">Máximo 20 prêmios</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-1">Publicações</label>
          <textarea
            value={data.publications}
            onChange={(e) => onChange({ publications: e.target.value })}
            maxLength={1000}
            rows={3}
            placeholder="Liste suas publicações científicas, artigos, etc."
            className="w-full px-3 py-2 border rounded-lg bg-background border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none transition-colors"
          />
        </div>
      </div>
    </Card>
  );
}

interface SpecialtiesSectionProps {
  doctorId: string;
}

function SpecialtiesSection({ doctorId }: SpecialtiesSectionProps) {
  const { data: specialties, isLoading } = useDoctorSpecialties(doctorId);
  const addMutation = useAddDoctorSpecialty(doctorId);
  const removeMutation = useRemoveDoctorSpecialty(doctorId);
  const setPrimaryMutation = useSetDoctorPrimarySpecialty(doctorId);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [selectedSpecialtyId, setSelectedSpecialtyId] = useState<string>('');
  const [setAsPrimary, setSetAsPrimary] = useState(false);
  const [proofSpecialty, setProofSpecialty] = useState<any | null>(null);
  const [rqeValue, setRqeValue] = useState('');
  const [proofUrl, setProofUrl] = useState('');
  const [isSubmittingProof, setIsSubmittingProof] = useState(false);
  const [isUploadingProofFile, setIsUploadingProofFile] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);

  const { data: allSpecialties } = useQuery({
    queryKey: ['all-specialties'],
    queryFn: () => doctorsApi.getSpecialties(),
  });

  const existingSpecialtyIds = new Set(specialties?.map((s) => s.specialtyId) || []);
  const availableSpecialties = allSpecialties?.filter((s) => !existingSpecialtyIds.has(s.id)) || [];

  const handleUploadProofFile = async (file: File) => {
    if (file.size > 15 * 1024 * 1024) {
      toast({
        title: 'Arquivo muito grande',
        description: 'O comprovante deve ter no máximo 15MB.',
        variant: 'error',
      });
      return;
    }
    setIsUploadingProofFile(true);
    try {
      const uploaded = await filesApi.uploadFile(file, doctorId, 'DOCTOR', 'DOCUMENT');
      const finalUrl = uploaded.url || (uploaded.key ? filesApi.getFileUrl(uploaded.key) : uploaded.id);
      setProofUrl(finalUrl);
      setUploadedFileName(file.name);
      toast({
        title: 'Arquivo anexado com sucesso!',
        description: file.name,
      });
    } catch (err: any) {
      toast({
        title: 'Falha no upload do anexo',
        description: err?.message || 'Tente novamente.',
        variant: 'error',
      });
    } finally {
      setIsUploadingProofFile(false);
    }
  };

  const handleAdd = async () => {
    if (!selectedSpecialtyId) return;
    try {
      await addMutation.mutateAsync({
        specialtyId: selectedSpecialtyId,
        primary: setAsPrimary,
      });
      setSelectedSpecialtyId('');
      setSetAsPrimary(false);
      queryClient.invalidateQueries({ queryKey: ['doctor-specialties'] });
    } catch (err) {
      console.error('Failed to add specialty:', err);
    }
  };

  const handleRemove = async (specialtyId: string) => {
    try {
      await removeMutation.mutateAsync(specialtyId);
      queryClient.invalidateQueries({ queryKey: ['doctor-specialties'] });
    } catch (err) {
      console.error('Failed to remove specialty:', err);
    }
  };

  const handleSetPrimary = async (specialtyId: string) => {
    try {
      await setPrimaryMutation.mutateAsync(specialtyId);
      queryClient.invalidateQueries({ queryKey: ['doctor-specialties'] });
    } catch (err) {
      console.error('Failed to set primary specialty:', err);
    }
  };

  const handleSubmitProof = async () => {
    if (!proofSpecialty) return;
    setIsSubmittingProof(true);
    try {
      await doctorsApi.submitSpecialtyProof(proofSpecialty.specialtyId, {
        rqe: rqeValue.trim() || undefined,
        proofDocumentUrl: proofUrl.trim() || undefined,
      });
      toast({
        title: 'Comprovante / RQE enviado!',
        description: 'Os dados foram submetidos para conferência da equipe médica da Medikei.',
      });
      setProofSpecialty(null);
      setRqeValue('');
      setProofUrl('');
      setUploadedFileName(null);
      queryClient.invalidateQueries({ queryKey: ['doctor-specialties'] });
    } catch (err: any) {
      toast({
        title: 'Erro ao enviar comprovante',
        description: err?.message || 'Tente novamente.',
        variant: 'error',
      });
    } finally {
      setIsSubmittingProof(false);
    }
  };

  if (isLoading) {
    return (
      <Card className="p-6 bg-card border-border">
        <Skeleton className="h-6 w-32 mb-4" />
        <div className="space-y-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6 bg-card border-border">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Stethoscope className="text-primary-500 h-5 w-5" />
          <h3 className="font-bold text-foreground text-lg">Especialidades & Habilitação de Atendimento</h3>
        </div>
        <span className="text-xs font-semibold text-muted-foreground bg-muted/40 px-2.5 py-1 rounded-lg border border-border">
          Validação por RQE
        </span>
      </div>

      {/* Banner de Regra de Negócio */}
      <div className="p-3.5 bg-primary/5 border border-primary/20 rounded-xl mb-5 text-xs text-muted-foreground leading-relaxed flex items-start gap-2.5">
        <Shield className="h-4 w-4 text-primary shrink-0 mt-0.5" />
        <span>
          <strong className="text-foreground">Regra Regulatória:</strong> Todo médico cadastrado é habilitado automaticamente para <strong>Clínico Geral</strong> e <strong>Consultas Expressas</strong>. As modalidades de atendimento (Telemedicina, Presencial e Domiciliar) de outras especialidades só são ativadas após envio e aprovação do comprovante ou número de <strong>RQE</strong>.
        </span>
      </div>

      <div className="space-y-4">
        {specialties && specialties.length > 0 ? (
          <div className="space-y-3">
            {specialties.map((ds) => {
              const specName = (ds.specialty?.name || (ds as any).name || '').toString();
              const cleanSpec = specName
                .toLowerCase()
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .trim();
              const isGeneral =
                cleanSpec.includes('geral') ||
                cleanSpec.includes('general') ||
                cleanSpec.includes('clinica geral') ||
                cleanSpec.includes('clinico geral') ||
                cleanSpec === 'clinica geral';

              const isApproved = isGeneral || ds.status === 'APPROVED';
              const isRejected = !isGeneral && ds.status === 'REJECTED';

              return (
                <div
                  key={ds.id}
                  className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-xl border border-border bg-card shadow-xs hover:border-primary/30 transition-all"
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-foreground text-sm">{ds.specialty.name}</span>
                      {ds.primary || isGeneral ? (
                        <Badge className="bg-primary text-primary-foreground text-[10px] font-bold">Principal</Badge>
                      ) : (
                        <Badge variant="outline" className="text-muted-foreground text-[10px]">
                          Secundária
                        </Badge>
                      )}

                      {isGeneral ? (
                        <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-2.5 py-0.5 rounded-full border border-emerald-500/20">
                          <CheckCircle className="h-3 w-3" /> Habilitado por CRM (Generalista)
                        </span>
                      ) : isApproved ? (
                        <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-2.5 py-0.5 rounded-full border border-emerald-500/20">
                          <CheckCircle className="h-3 w-3" /> Habilitado
                        </span>
                      ) : isRejected ? (
                        <span className="inline-flex items-center gap-1 text-[11px] font-bold text-destructive bg-destructive/10 px-2.5 py-0.5 rounded-full border border-destructive/20">
                          <XCircle className="h-3 w-3" /> Recusado
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-600 dark:text-amber-400 bg-amber-500/10 px-2.5 py-0.5 rounded-full border border-amber-500/20">
                          <Clock className="h-3 w-3" /> Aguardando Comprovação
                        </span>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground pt-1">
                      {isGeneral ? (
                        <span className="text-emerald-600 dark:text-emerald-400 font-semibold text-[11px]">
                          ✓ Prerrogativa médica nata liberada para Clínica Geral e Plantão 24h
                        </span>
                      ) : isApproved ? (
                        <span className="text-emerald-600 dark:text-emerald-400 font-semibold text-[11px]">
                          ✓ Telemedicina, Presencial e Domiciliar liberados
                        </span>
                      ) : (
                        <span className="text-amber-600 dark:text-amber-400 font-medium text-[11px]">
                          Comprove o RQE para ativar as modalidades desta especialidade
                        </span>
                      )}
                      {ds.rqe && (
                        <span className="font-mono text-[11px] bg-muted/60 px-1.5 py-0.5 rounded">
                          RQE: {ds.rqe}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    {!isGeneral && !isApproved && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setProofSpecialty(ds);
                          setRqeValue(ds.rqe || '');
                          setProofUrl(ds.proofDocumentUrl || '');
                          setUploadedFileName(null);
                        }}
                        className="text-xs h-8 rounded-lg font-bold border-amber-500/30 text-amber-700 dark:text-amber-300 hover:bg-amber-500/10 cursor-pointer"
                      >
                        Comprovar RQE
                      </Button>
                    )}

                    {!isGeneral && !ds.primary && isApproved && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSetPrimary(ds.specialtyId)}
                        disabled={setPrimaryMutation.isPending}
                        className="text-xs h-8 cursor-pointer"
                      >
                        Definir como principal
                      </Button>
                    )}

                    {!isGeneral && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemove(ds.specialtyId)}
                        disabled={removeMutation.isPending}
                        className="h-8 w-8 text-destructive hover:text-destructive cursor-pointer"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground py-4 text-center">
            Nenhuma especialidade adicionada ainda.
          </p>
        )}

        {/* Modal de Comprovação de RQE */}
        {proofSpecialty && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm animate-in fade-in">
            <div className="bg-card border border-border rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4">
              <div className="flex items-center justify-between pb-3 border-b border-border">
                <div className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-primary" />
                  <h4 className="font-bold text-base text-foreground">
                    Comprovar {proofSpecialty.specialty?.name}
                  </h4>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setProofSpecialty(null);
                    setUploadedFileName(null);
                  }}
                  className="p-1.5 text-muted-foreground hover:text-foreground rounded-lg transition-colors cursor-pointer"
                >
                  <XCircle className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-3">
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Informe o número do seu <strong>RQE</strong> registrado no CRM ou anexe o comprovante (diploma de residência ou título de especialista) para habilitar os atendimentos de {proofSpecialty.specialty?.name}.
                </p>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                    Número do RQE (Registro de Qualificação de Especialista)
                  </label>
                  <Input
                    placeholder="Ex: 12345"
                    value={rqeValue}
                    onChange={(e) => setRqeValue(e.target.value)}
                    className="rounded-xl h-11"
                  />
                </div>

                {/* Campo de Anexo de Arquivo */}
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5 flex items-center justify-between">
                    <span>Anexar Comprovante / Certificado (Opcional)</span>
                    <span className="text-[11px] font-normal text-muted-foreground">PDF, PNG ou JPG (até 15MB)</span>
                  </label>
                  {uploadedFileName ? (
                    <div className="flex items-center justify-between p-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 text-xs">
                      <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300 font-medium truncate">
                        <Paperclip className="h-4 w-4 shrink-0 text-emerald-600" />
                        <span className="truncate">{uploadedFileName}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setUploadedFileName(null);
                          setProofUrl('');
                        }}
                        className="text-xs text-destructive hover:underline ml-2 shrink-0 cursor-pointer font-semibold"
                      >
                        Remover
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center p-4 border-2 border-dashed border-border hover:border-primary/50 rounded-xl cursor-pointer bg-background hover:bg-muted/40 transition-colors">
                      <input
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg"
                        className="hidden"
                        disabled={isUploadingProofFile}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleUploadProofFile(file);
                        }}
                      />
                      {isUploadingProofFile ? (
                        <div className="flex items-center gap-2 text-xs text-primary font-medium">
                          <div className="h-4 w-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                          <span>Enviando arquivo...</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Upload className="h-4 w-4 text-primary" />
                          <span className="font-medium text-foreground">Clique para selecionar anexo</span>
                        </div>
                      )}
                    </label>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                    Ou informe Link Externo do Comprovante (Opcional)
                  </label>
                  <Input
                    placeholder="https://..."
                    value={proofUrl}
                    onChange={(e) => setProofUrl(e.target.value)}
                    className="rounded-xl h-11"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-border">
                <Button
                  variant="outline"
                  onClick={() => {
                    setProofSpecialty(null);
                    setUploadedFileName(null);
                  }}
                  className="rounded-xl h-10 px-4"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleSubmitProof}
                  disabled={(!rqeValue.trim() && !proofUrl.trim()) || isSubmittingProof || isUploadingProofFile}
                  className="rounded-xl h-10 px-5 font-bold"
                >
                  {isSubmittingProof ? 'Enviando...' : 'Submeter para Aprovação'}
                </Button>
              </div>
            </div>
          </div>
        )}

        <div className="border-t border-border pt-4 mt-4">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-3">
            <div className="flex-1 space-y-1">
              <label className="text-sm font-medium text-foreground">Adicionar especialidade</label>
              <Select
                value={selectedSpecialtyId}
                onValueChange={setSelectedSpecialtyId}
              >
                <SelectTrigger className="bg-background border-border text-foreground">
                  <SelectValue placeholder="Selecione uma especialidade" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {availableSpecialties.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="set-primary"
                  checked={setAsPrimary}
                  onCheckedChange={(checked) => setSetAsPrimary(checked === true)}
                />
                <label htmlFor="set-primary" className="text-sm text-foreground cursor-pointer">
                  Principal
                </label>
              </div>
              <Button
                onClick={handleAdd}
                disabled={!selectedSpecialtyId || addMutation.isPending}
              >
                {addMutation.isPending ? 'Adicionando...' : 'Adicionar'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}

interface DoctorLocationModalitiesSectionProps {
  data: ProfileFormData;
  onChange: (data: Partial<ProfileFormData>) => void;
  loading?: boolean;
}

function DoctorLocationModalitiesSection({ data, onChange, loading }: DoctorLocationModalitiesSectionProps) {
  const [cepLoading, setCepLoading] = useState(false);

  const handleCepBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    const rawVal = e.target.value;
    const clean = rawVal.replace(/\D/g, '');
    if (clean.length === 8) {
      setCepLoading(true);
      try {
        const res = await fetch(`https://viacep.com.br/ws/${clean}/json/`);
        const json = await res.json();
        if (!json.erro) {
          onChange({
            clinicAddress: json.logradouro || data.clinicAddress,
            clinicNeighborhood: json.bairro || data.clinicNeighborhood,
            clinicCity: json.localidade || data.clinicCity,
            clinicState: json.uf || data.clinicState,
            clinicCep: json.cep || rawVal,
          });
        }
      } catch (err) {
        console.error('Erro ao consultar CEP:', err);
      } finally {
        setCepLoading(false);
      }
    }
  };

  if (loading) {
    return (
      <Card className="p-6 bg-card border-border">
        <Skeleton className="h-6 w-48 mb-4" />
        <Skeleton className="h-24 w-full" />
      </Card>
    );
  }

  const isLocationRequired = Boolean(data.hasInPerson || data.hasHomeCare);

  return (
    <Card className="p-6 bg-card border-border space-y-6">
      <div className="flex items-center gap-2">
        <MapPin className="text-primary-500 h-5 w-5" />
        <h3 className="font-semibold text-foreground">Modalidades de Atendimento & Localização</h3>
        <Badge variant="outline" className="ml-auto bg-primary-500/10 text-primary-600 border-primary-500/30">
          Geolocalização Ativa
        </Badge>
      </div>

      <p className="text-sm text-muted-foreground">
        Defina como você atende seus pacientes. Para consultas presenciais e domiciliares, o sistema usará seu endereço e raio de cobertura para exibir seu perfil nas buscas por proximidade e geolocalização.
      </p>

      {/* Opções de Modalidade */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Telemedicina */}
        <div
          onClick={() => onChange({ hasTelemedicine: !data.hasTelemedicine })}
          className={`flex flex-col p-4 rounded-xl border cursor-pointer transition-all ${
            data.hasTelemedicine
              ? 'border-primary bg-primary/5 dark:bg-primary/10 shadow-sm'
              : 'border-border hover:border-muted-foreground/30 bg-card'
          }`}
        >
          <div className="flex items-center justify-between mb-2">
            <Video className={`h-5 w-5 ${data.hasTelemedicine ? 'text-primary' : 'text-muted-foreground'}`} />
            <Checkbox
              checked={data.hasTelemedicine ?? true}
              onCheckedChange={(checked) => onChange({ hasTelemedicine: checked === true })}
            />
          </div>
          <span className="font-semibold text-foreground text-sm">Telemedicina</span>
          <span className="text-xs text-muted-foreground mt-1">
            Atendimento online por vídeo para todo o Brasil.
          </span>
        </div>

        {/* Presencial em Consultório */}
        <div
          onClick={() => onChange({ hasInPerson: !data.hasInPerson })}
          className={`flex flex-col p-4 rounded-xl border cursor-pointer transition-all ${
            data.hasInPerson
              ? 'border-primary bg-primary/5 dark:bg-primary/10 shadow-sm'
              : 'border-border hover:border-muted-foreground/30 bg-card'
          }`}
        >
          <div className="flex items-center justify-between mb-2">
            <Building className={`h-5 w-5 ${data.hasInPerson ? 'text-primary' : 'text-muted-foreground'}`} />
            <Checkbox
              checked={data.hasInPerson ?? false}
              onCheckedChange={(checked) => onChange({ hasInPerson: checked === true })}
            />
          </div>
          <span className="font-semibold text-foreground text-sm">Presencial (Consultório)</span>
          <span className="text-xs text-muted-foreground mt-1">
            O paciente vai até sua clínica ou consultório físico.
          </span>
        </div>

        {/* Atendimento Domiciliar */}
        <div
          onClick={() => onChange({ hasHomeCare: !data.hasHomeCare })}
          className={`flex flex-col p-4 rounded-xl border cursor-pointer transition-all ${
            data.hasHomeCare
              ? 'border-primary bg-primary/5 dark:bg-primary/10 shadow-sm'
              : 'border-border hover:border-muted-foreground/30 bg-card'
          }`}
        >
          <div className="flex items-center justify-between mb-2">
            <Home className={`h-5 w-5 ${data.hasHomeCare ? 'text-primary' : 'text-muted-foreground'}`} />
            <Checkbox
              checked={data.hasHomeCare ?? false}
              onCheckedChange={(checked) => onChange({ hasHomeCare: checked === true })}
            />
          </div>
          <span className="font-semibold text-foreground text-sm">Atendimento Domiciliar</span>
          <span className="text-xs text-muted-foreground mt-1">
            Você vai até a residência do paciente dentro do seu raio.
          </span>
        </div>
      </div>

      {/* Endereço Base / Consultório */}
      {isLocationRequired && (
        <div className="pt-4 border-t border-border space-y-4">
          <h4 className="font-medium text-sm text-foreground flex items-center gap-2">
            <Navigation className="h-4 w-4 text-primary" />
            Endereço Base / Consultório
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2 space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Nome do Local / Clínica (Opcional)</label>
              <input
                type="text"
                value={data.clinicName || ''}
                onChange={(e) => onChange({ clinicName: e.target.value })}
                placeholder="Ex: Consultório Dr. Silva, Edifício Medical Center"
                className="w-full h-10 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground flex items-center justify-between">
                <span>CEP</span>
                {cepLoading && <span className="text-xs text-primary animate-pulse">Buscando...</span>}
              </label>
              <input
                type="text"
                value={data.clinicCep || ''}
                onChange={(e) => onChange({ clinicCep: e.target.value })}
                onBlur={handleCepBlur}
                placeholder="00000-000"
                maxLength={9}
                className="w-full h-10 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-3 space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Logradouro / Rua</label>
              <input
                type="text"
                value={data.clinicAddress || ''}
                onChange={(e) => onChange({ clinicAddress: e.target.value })}
                placeholder="Av. Paulista, Rua das Flores..."
                className="w-full h-10 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Número</label>
              <input
                type="text"
                value={data.clinicNumber || ''}
                onChange={(e) => onChange({ clinicNumber: e.target.value })}
                placeholder="123"
                className="w-full h-10 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Complemento / Sala</label>
              <input
                type="text"
                value={data.clinicComplement || ''}
                onChange={(e) => onChange({ clinicComplement: e.target.value })}
                placeholder="Sala 402, Bloco B"
                className="w-full h-10 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Bairro</label>
              <input
                type="text"
                value={data.clinicNeighborhood || ''}
                onChange={(e) => onChange({ clinicNeighborhood: e.target.value })}
                placeholder="Bela Vista"
                className="w-full h-10 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2 space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Cidade</label>
                <input
                  type="text"
                  value={data.clinicCity || ''}
                  onChange={(e) => onChange({ clinicCity: e.target.value })}
                  placeholder="São Paulo"
                  className="w-full h-10 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">UF</label>
                <input
                  type="text"
                  value={data.clinicState || ''}
                  onChange={(e) => onChange({ clinicState: e.target.value.toUpperCase() })}
                  placeholder="SP"
                  maxLength={2}
                  className="w-full h-10 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary uppercase text-center"
                />
              </div>
            </div>
          </div>

          {/* Configuração de Raio de Proximidade */}
          <div className="pt-4 border-t border-border/60 grid grid-cols-1 md:grid-cols-2 gap-6">
            {data.hasHomeCare && (
              <div className="p-4 rounded-xl bg-muted/40 border border-border space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-sm text-foreground flex items-center gap-1.5">
                    <Home className="h-4 w-4 text-primary" />
                    Raio para Domiciliar
                  </span>
                  <Badge variant="outline" className="font-bold text-primary border-primary/30">
                    {data.homeCareRadiusKm || 15} km
                  </Badge>
                </div>
                <input
                  type="range"
                  min={2}
                  max={50}
                  step={1}
                  value={data.homeCareRadiusKm || 15}
                  onChange={(e) => onChange({ homeCareRadiusKm: Number(e.target.value) })}
                  className="w-full cursor-pointer accent-primary"
                />
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Pacientes fora do raio de <strong>{data.homeCareRadiusKm || 15} km</strong> não verão seu perfil para atendimento em domicílio (blindagem geográfica por raio de cobertura).
                </p>
              </div>
            )}

            {data.hasInPerson && (
              <div className="p-4 rounded-xl bg-muted/40 border border-border space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-sm text-foreground flex items-center gap-1.5">
                    <Building className="h-4 w-4 text-primary" />
                    Abrangência Presencial
                  </span>
                  <Badge variant="outline" className="font-bold text-primary border-primary/30">
                    {data.inPersonRadiusKm || 30} km
                  </Badge>
                </div>
                <input
                  type="range"
                  min={5}
                  max={100}
                  step={5}
                  value={data.inPersonRadiusKm || 30}
                  onChange={(e) => onChange({ inPersonRadiusKm: Number(e.target.value) })}
                  className="w-full cursor-pointer accent-primary"
                />
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Seu consultório será recomendado prioritariamente a pacientes situados em até <strong>{data.inPersonRadiusKm || 30} km</strong>.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

export function DoctorProfileEditPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [coverUrl, setCoverUrl] = useState<string | null>(null);

  const { data: profile, isLoading: profileLoading } = useDoctorProfile(user?.id || '');
  const doctorId = profile?.id || user?.id || '';

  const [formData, setFormData] = useState<ProfileFormData>({
    displayName: '',
    tagline: '',
    bio: '',
    coverUrl: null,
    languages: ['Português'],
    awards: [],
    publications: '',
    minAge: null,
    maxAge: null,
    allowedAgeGroups: [],
    hasTelemedicine: true,
    hasInPerson: false,
    hasHomeCare: false,
    clinicName: '',
    clinicAddress: '',
    clinicNumber: '',
    clinicComplement: '',
    clinicNeighborhood: '',
    clinicCity: '',
    clinicState: '',
    clinicCep: '',
    homeCareRadiusKm: 15,
    inPersonRadiusKm: 30,
  });

  useEffect(() => {
    if (profile) {
      setFormData({
        displayName: profile.displayName || '',
        tagline: profile.tagline || '',
        bio: profile.bio || '',
        languages: profile.languages || ['Português'],
        awards: profile.awards || [],
        publications: profile.publications || '',
        minAge: profile.minAge ?? null,
        maxAge: profile.maxAge ?? null,
        allowedAgeGroups: profile.allowedAgeGroups || [],
        hasTelemedicine: profile.hasTelemedicine ?? true,
        hasInPerson: profile.hasInPerson ?? false,
        hasHomeCare: profile.hasHomeCare ?? false,
        clinicName: profile.clinicName || '',
        clinicAddress: profile.clinicAddress || '',
        clinicNumber: profile.clinicNumber || '',
        clinicComplement: profile.clinicComplement || '',
        clinicNeighborhood: profile.clinicNeighborhood || '',
        clinicCity: profile.clinicCity || '',
        clinicState: profile.clinicState || '',
        clinicCep: profile.clinicCep || '',
        homeCareRadiusKm: profile.homeCareRadiusKm ?? 15,
        inPersonRadiusKm: profile.inPersonRadiusKm ?? 30,
      });
      setPhotoUrl(profile.photoUrl || null);
      setCoverUrl(profile.coverUrl || null);
    }
  }, [profile]);

  useEffect(() => {
    const hash = window.location.hash;
    if (hash === '#documentos' || hash === '#publico-atendido') {
      const timer = setTimeout(() => {
        const id = hash.replace('#', '');
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
        }
      }, 250);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, []);

  const handleFormChange = (data: Partial<ProfileFormData>) => {
    setFormData((prev) => ({ ...prev, ...data }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload: Partial<DoctorProfile> = {
        displayName: formData.displayName?.trim() || undefined,
        tagline: formData.tagline?.trim() || undefined,
        bio: formData.bio?.trim() || undefined,
        photoUrl: photoUrl !== null && photoUrl !== '' ? photoUrl : (photoUrl === '' ? null : undefined),
        coverUrl: coverUrl !== null && coverUrl !== '' ? coverUrl : (coverUrl === '' ? null : undefined),
        languages: formData.languages,
        awards: formData.awards,
        publications: formData.publications?.trim() || undefined,
        minAge: typeof formData.minAge === 'number' ? formData.minAge : undefined,
        maxAge: typeof formData.maxAge === 'number' ? formData.maxAge : undefined,
        allowedAgeGroups: formData.allowedAgeGroups,
        hasTelemedicine: formData.hasTelemedicine,
        hasInPerson: formData.hasInPerson,
        hasHomeCare: formData.hasHomeCare,
        clinicName: formData.clinicName?.trim() || undefined,
        clinicAddress: formData.clinicAddress?.trim() || undefined,
        clinicNumber: formData.clinicNumber?.trim() || undefined,
        clinicComplement: formData.clinicComplement?.trim() || undefined,
        clinicNeighborhood: formData.clinicNeighborhood?.trim() || undefined,
        clinicCity: formData.clinicCity?.trim() || undefined,
        clinicState: formData.clinicState ? formData.clinicState.trim().slice(0, 2).toUpperCase() : undefined,
        clinicCep: formData.clinicCep?.trim() || undefined,
        homeCareRadiusKm: typeof formData.homeCareRadiusKm === 'number' ? formData.homeCareRadiusKm : undefined,
        inPersonRadiusKm: typeof formData.inPersonRadiusKm === 'number' ? formData.inPersonRadiusKm : undefined,
      };

      const updated = await doctorsApi.updateMyProfile(payload);

      if (updated) {
        if (doctorId) {
          queryClient.setQueryData(['doctor', doctorId, 'profile'], updated);
        }
        if (user?.id) {
          queryClient.setQueryData(['doctor', user.id, 'profile'], updated);
        }
        queryClient.setQueryData(['doctor', 'profile'], updated);
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['doctor', doctorId, 'profile'] }),
        queryClient.invalidateQueries({ queryKey: ['doctor', 'profile'] }),
        queryClient.invalidateQueries({ queryKey: ['doctor', user?.id, 'profile'] }),
        queryClient.invalidateQueries({ queryKey: ['doctor-my-settings'] }),
      ]);

      toast({
        title: 'Perfil salvo!',
        description: 'Suas informações foram atualizadas com sucesso.',
      });
    } catch (err: any) {
      console.error('Failed to save profile:', err);
      let message = 'Não foi possível salvar as alterações.';
      if (err?.message) {
        message = err.message;
      } else if (err?.error?.message) {
        message = Array.isArray(err.error.message) ? err.error.message.join(', ') : err.error.message;
      }
      toast({
        title: 'Erro ao salvar perfil',
        description: message,
        variant: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoChange = useCallback((url: string) => {
    setPhotoUrl(url);
  }, []);

  const handleCoverChange = useCallback((url: string) => {
    setCoverUrl(url);
  }, []);

  const realCrm = profile?.crm || (user as any)?.crm || '';
  const realCrmState = profile?.crmState || (user as any)?.crmState || '';
  const doctorStatus = profile?.status || (user as any)?.status || 'APPROVED';

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/medico/dashboard')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-foreground">Editar Perfil</h1>
            <p className="text-muted-foreground mt-1">
              Gerencie suas informações profissionais
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2.5 flex-wrap">
          <Button
            type="button"
            variant="outline"
            onClick={() => setIsPreviewOpen(true)}
            className="rounded-xl font-bold gap-1.5 border-primary/30 text-primary hover:bg-primary/5 cursor-pointer shadow-xs"
          >
            <Sparkles className="h-4 w-4" />
            <span>Pré-visualizar Perfil Público</span>
          </Button>
          <Button onClick={handleSave} disabled={saving} className="rounded-xl font-bold shadow-sm">
            {saving ? (
              'Salvando...'
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Salvar Alterações
              </>
            )}
          </Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          <ProfileSection
            data={formData}
            onChange={handleFormChange}
            loading={profileLoading}
            photoUrl={photoUrl}
            onPhotoChange={handlePhotoChange}
            coverUrl={coverUrl}
            onCoverChange={handleCoverChange}
            doctorId={doctorId}
          />
          <SpecialtiesSection doctorId={doctorId} />
          <DoctorLocationModalitiesSection
            data={formData}
            onChange={handleFormChange}
            loading={profileLoading}
          />
          <DoctorTargetAudienceSection
            minAge={formData.minAge}
            maxAge={formData.maxAge}
            allowedAgeGroups={formData.allowedAgeGroups}
            onChange={(audience) => handleFormChange(audience)}
          />
          <DoctorDocumentsSection doctorId={doctorId} />
        </div>
        <div className="space-y-6">
          <IdentitySection
            crm={realCrm}
            crmState={realCrmState}
            status={doctorStatus}
            loading={profileLoading}
          />
        </div>
      </div>

      {/* Modal de Pré-visualização de Alta Fidelidade do Perfil */}
      <DoctorProfilePreviewModal
        open={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        data={{
          displayName: formData.displayName,
          tagline: formData.tagline,
          bio: formData.bio,
          photoUrl: photoUrl || profile?.photoUrl,
          coverUrl: coverUrl || profile?.coverUrl,
          crm: realCrm,
          crmState: realCrmState,
          specialty: (profile as any)?.specialty || 'Clínica Geral',
          specialties: (profile as any)?.specialties || [],
          languages: formData.languages,
          awards: formData.awards,
          publications: formData.publications,
          minAge: formData.minAge,
          maxAge: formData.maxAge,
          allowedAgeGroups: formData.allowedAgeGroups,
          hasTelemedicine: formData.hasTelemedicine,
          hasInPerson: formData.hasInPerson,
          hasHomeCare: formData.hasHomeCare,
          clinicName: formData.clinicName,
          clinicAddress: formData.clinicAddress,
          clinicCity: formData.clinicCity,
          clinicState: formData.clinicState,
          homeCareRadiusKm: formData.homeCareRadiusKm,
        }}
      />
    </div>
  );
}
